import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const BulkRenewalModal = ({ selectedClients, subscriptions, onClose, onRenewal }) => {
  const [renewalData, setRenewalData] = useState({
    duration: '1',
    durationType: 'month',
    subscriptionType: 'keep-current',
    newSubscriptionType: 'Premium Monthly',
    startDate: new Date().toISOString().split('T')[0]
  });

  const selectedSubscriptions = subscriptions.filter(sub => 
    selectedClients.includes(sub.id)
  );

  const subscriptionTypes = [
    'Basic Monthly',
    'Premium Monthly',
    'Basic Yearly',
    'Premium Yearly',
    'VIP Monthly',
    'VIP Yearly'
  ];

  const handleInputChange = (field, value) => {
    setRenewalData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateNewEndDate = (startDate, duration, durationType) => {
    const start = new Date(startDate);
    const durationNum = parseInt(duration);
    
    if (durationType === 'month') {
      start.setMonth(start.getMonth() + durationNum);
    } else {
      start.setFullYear(start.getFullYear() + durationNum);
    }
    
    return start;
  };

  const handleRenewal = () => {
    console.log('Bulk renewal data:', {
      clients: selectedSubscriptions,
      renewalData
    });
    
    // Mock renewal process
    onRenewal();
  };

  const newEndDate = calculateNewEndDate(
    renewalData.startDate,
    renewalData.duration,
    renewalData.durationType
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Bulk Renewal ({selectedClients.length} clients)
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Selected Clients */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Selected Clients</h4>
            <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto">
              <div className="space-y-2">
                {selectedSubscriptions.map((subscription) => (
                  <div key={subscription.id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Image
                        src={subscription.avatar}
                        alt={subscription.clientName}
                        className="w-8 h-8 rounded-full mr-3"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {subscription.clientName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {subscription.subscriptionType}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      Expires: {subscription.endDate.toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Renewal Settings */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Renewal Duration
              </label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={renewalData.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                />
                <select
                  value={renewalData.durationType}
                  onChange={(e) => handleInputChange('durationType', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                >
                  <option value="month">Month(s)</option>
                  <option value="year">Year(s)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription Type
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="subscriptionType"
                    value="keep-current"
                    checked={renewalData.subscriptionType === 'keep-current'}
                    onChange={(e) => handleInputChange('subscriptionType', e.target.value)}
                    className="text-green-600 focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Keep current subscription types</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="subscriptionType"
                    value="change-all"
                    checked={renewalData.subscriptionType === 'change-all'}
                    onChange={(e) => handleInputChange('subscriptionType', e.target.value)}
                    className="text-green-600 focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Change all to same type</span>
                </label>
              </div>

              {renewalData.subscriptionType === 'change-all' && (
                <div className="mt-3">
                  <select
                    value={renewalData.newSubscriptionType}
                    onChange={(e) => handleInputChange('newSubscriptionType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  >
                    {subscriptionTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Renewal Start Date
              </label>
              <input
                type="date"
                value={renewalData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* Preview */}
            <div className="bg-green-50 rounded-lg p-4">
              <h5 className="text-sm font-medium text-green-900 mb-2">Renewal Preview</h5>
              <div className="text-sm text-green-800">
                <p>Duration: {renewalData.duration} {renewalData.durationType}(s)</p>
                <p>New end date: {newEndDate.toLocaleDateString()}</p>
                <p>
                  Subscription type: {
                    renewalData.subscriptionType === 'keep-current' ?'Keep current types' 
                      : renewalData.newSubscriptionType
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleRenewal}
            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700"
          >
            <Icon name="RefreshCw" size={16} className="mr-2 inline" />
            Renew All Subscriptions
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkRenewalModal;